export interface LinqxConfig {
  apiHost: string;
  authWebUrl: string;
  authServerUrl: string;
  urls: {
    website: string;
    terms: string;
    privacy: string;
  };
}

/**
 * External values provider — allows the host app to inject env values
 * at runtime (e.g. from Expo Constants.expoConfig.extra).
 */
let externalValues: Record<string, string | undefined> = {};

/**
 * Register external values that override process.env lookups.
 * Call this early in the app lifecycle with values from the host platform.
 *
 * @example
 * // In Expo _layout.tsx:
 * import Constants from 'expo-constants';
 * registerConfigValues(Constants.expoConfig?.extra ?? {});
 */
export const registerConfigValues = (values: Record<string, unknown>): void => {
  externalValues = {};
  for (const [key, value] of Object.entries(values)) {
    if (typeof value === "string" && value) {
      externalValues[key] = value;
    }
  }
  currentConfig = buildConfig();
};

/**
 * Get environment variable value.
 *
 * Priority order:
 * 1. External values (from registerConfigValues — e.g. Expo extra)
 * 2. process.env with EXPO_PUBLIC_ prefix
 * 3. process.env with NEXT_PUBLIC_ prefix
 * 4. process.env unprefixed
 *
 * Returns empty string if not found — no hidden defaults.
 */
const getEnvVar = (extraKey: string, envSuffix: string): string => {
  if (externalValues[extraKey]) {
    return externalValues[extraKey]!;
  }

  if (typeof process !== "undefined" && process.env) {
    const expoKey = `EXPO_PUBLIC_${envSuffix}`;
    if (process.env[expoKey]) return process.env[expoKey] as string;

    const nextKey = `NEXT_PUBLIC_${envSuffix}`;
    if (process.env[nextKey]) return process.env[nextKey] as string;

    if (process.env[envSuffix]) return process.env[envSuffix] as string;
  }

  return "";
};

const buildConfig = (): LinqxConfig => ({
  apiHost: getEnvVar("apiHost", "API_HOST"),
  authWebUrl: getEnvVar("authWebUrl", "AUTH_WEB_URL"),
  authServerUrl: getEnvVar("authServerUrl", "AUTH_SERVER_URL"),
  urls: {
    website: getEnvVar("websiteUrl", "WEBSITE_URL"),
    terms: getEnvVar("termsUrl", "TERMS_URL"),
    privacy: getEnvVar("privacyUrl", "PRIVACY_URL"),
  },
});

let currentConfig: LinqxConfig = buildConfig();

export const getConfig = (): LinqxConfig => currentConfig;

export const setConfig = (config: Partial<LinqxConfig>): void => {
  currentConfig = {
    ...currentConfig,
    ...config,
    urls: {
      ...currentConfig.urls,
      ...config.urls,
    },
  };
};

export const resetConfig = (): void => {
  currentConfig = buildConfig();
};
